import { createContext, useContext } from "react";

import type { AssertionDisplayMode } from "../assertions";

import type { RequestLogDisplayMode, TraceDisplayMode } from "./utils";

import type { CellStatus, SmartColumn } from "@/queries/smart-tables/types";
import type { CellContentType, ClassifiedContent } from "@/routes/smart-table/utils/contentType";

export type CellContentDialogDisplayType =
  | CellContentType
  | AssertionDisplayMode
  | TraceDisplayMode
  | RequestLogDisplayMode
  | "compare_text";

export interface DisplayTypeOption {
  value: CellContentDialogDisplayType;
  label: string;
}

export interface CellContentDialogContextValue {
  column: SmartColumn | undefined;
  rowIndex: number;
  displayedRowIndex: number | null;
  totalRows: number;
  content: ClassifiedContent;
  sourceContentType: CellContentType;
  displayType: CellContentDialogDisplayType;
  setDisplayType: (type: CellContentDialogDisplayType) => void;
  displayTypeOptions?: DisplayTypeOption[];
  navigateUp: () => void;
  navigateDown: () => void;
  close: () => void;
  shouldShowComputationStatus: boolean;
  effectiveStatus: CellStatus;
  cellUpdatedAt?: Date | string | number | null;
  isSavingCell: boolean;
  isContentLoading: boolean;
  hideRowNavigation?: boolean;
  hideSavedAt?: boolean;
  headerContextLabel?: string;
  /** Snippet versions a prompt cell was computed with, e.g. "rules v3". */
  snippetVersionsLabel?: string;
}

const CellContentDialogContext = createContext<CellContentDialogContextValue | null>(null);

export function CellContentDialogProvider({
  value,
  children,
}: {
  value: CellContentDialogContextValue;
  children: React.ReactNode;
}) {
  return (
    <CellContentDialogContext.Provider value={value}>{children}</CellContentDialogContext.Provider>
  );
}

export function useCellContentDialogContext(): CellContentDialogContextValue {
  const context = useContext(CellContentDialogContext);
  if (!context) {
    throw new Error("useCellContentDialogContext must be used within CellContentDialogProvider");
  }

  return context;
}
